import { useMemo } from 'react'
import { cloneDeep } from 'lodash'

import { Button, Input, Label, SearchableSelect } from '@/ui'
import { PercentageIncomePerState } from '@/reducers/auth/userReducer'
import { UpdateStateType } from './TaxProfileForm'
import {
  databaseColumns as KEYS,
  generateUSAStateOptions,
  getUnusedOptions,
} from '@/features/taxes/taxConstants'
import { faTrash } from '@fortawesome/pro-regular-svg-icons'
interface RowProps {
  data: PercentageIncomePerState
  required: boolean
  onDeleteClicked: () => void
  onUSAStateChange: (_usaState: string) => void
  onPercentageChange: (_percentage: string) => void
  usaStateOptions: Array<{
    value: string
    text: string
  }>
  disabled: boolean
}

const Row = ({
  data,
  required,
  onDeleteClicked,
  onUSAStateChange,
  onPercentageChange,
  usaStateOptions,
  disabled,
}: RowProps) => (
  <div className="flex flex-row gap-4">
    <div className="w-full md:w-4/8">
      <Label className="text-neutral-500 italic">State</Label>
      <SearchableSelect
        options={usaStateOptions.map((option) => ({
          value: option.value,
          label: option.text,
        }))}
        disabled={disabled}
        value={data?.state}
        placeholder="State your client(s) live in"
        onChange={(value) => onUSAStateChange(value ?? '')}
      />
    </div>
    <div className="w-full md:w-3/8">
      <Label className="text-neutral-500 italic">% of income made</Label>
      <Input
        required
        className="w-full"
        maxLength={3}
        type="number"
        disabled={disabled}
        value={data?.value || ''}
        placeholder={
          data?.state
            ? `% of income made in ${data.state}`
            : 'Please enter the % income made'
        }
        onChange={(e) => onPercentageChange(e.target.value)}
      />
    </div>
    <div className="w-full md:w-1/8 flex items-end mt-2">
      {!required && (
        <Button
          variant="secondary"
          iconLeft={faTrash}
          size="md"
          onClick={onDeleteClicked}
        />
      )}
    </div>
  </div>
)

interface Props {
  rowData: PercentageIncomePerState[]
  updateState: (_props: UpdateStateType) => void
  error?: string
  disabled: boolean
}

export const IncomeByState = ({
  rowData,
  updateState,
  error,
  disabled,
}: Props) => {
  const usaStateOptions = useMemo(() => {
    const used = rowData?.map((data) => data.state || '') || []
    const all = generateUSAStateOptions()

    return getUnusedOptions({ used, all })
  }, [rowData])

  const createNewRow = () => {
    const clone = [...rowData]
    clone.push({})
    updateState({ key: KEYS.percentageIncomePerState, value: clone })
  }

  const deleteRow = (index: number) => {
    const clone = [...rowData]
    clone.splice(index, 1)
    updateState({ key: KEYS.percentageIncomePerState, value: clone })
  }

  const updateUSAState = (index: number, usaState: string) => {
    const clone = cloneDeep(rowData)
    clone[index].state = usaState
    updateState({ key: KEYS.percentageIncomePerState, value: clone })
  }

  const updatePercentageValue = (index: number, percentage: string) => {
    const clone = cloneDeep(rowData)
    clone[index].value = percentage
    updateState({ key: KEYS.percentageIncomePerState, value: clone })
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4">
        {rowData.map((row, i) => (
          <Row
            key={`row-${row.state}`}
            data={row}
            disabled={disabled}
            required={i === 0}
            onDeleteClicked={() => deleteRow(i)}
            onUSAStateChange={(usaState) => updateUSAState(i, usaState)}
            onPercentageChange={(percentage) =>
              updatePercentageValue(i, percentage)
            }
            usaStateOptions={usaStateOptions}
          />
        ))}
        {error && <Label error>{error}</Label>}
      </div>
      <div>
        <Button disabled={disabled} onClick={() => createNewRow()}>
          + Add another state
        </Button>
      </div>
    </div>
  )
}
